import {
  BITBUCKET_BASE_URL,
  GIT_REPO_NAMESPACE_POLICY,
  GIT_REPO_NAMESPACE_RULE,
  GIT_REPO_NAMESPACE_USER_FUNCTION,
} from '@/config'
import type { TableDataType } from '@/types'
import urlJoin from 'url-join'

/**
 * Bitbucket (a.k.a.: Stash) policies repositories base URL.
 */
const BITBUCKET_POLICY_REPO_BASE_URL =
  GIT_REPO_NAMESPACE_POLICY &&
  `${BITBUCKET_BASE_URL ?? ''}/projects/${encodeURIComponent(GIT_REPO_NAMESPACE_POLICY)}/repos`

/**
 * Bitbucket (a.k.a.: Stash) rules repositories base URL.
 */
const BITBUCKET_RULE_REPO_BASE_URL =
  GIT_REPO_NAMESPACE_RULE && `${BITBUCKET_BASE_URL ?? ''}/projects/${encodeURIComponent(GIT_REPO_NAMESPACE_RULE)}/repos`

/**
 * Bitbucket (a.k.a.: Stash) user functions repositories base URL.
 */
const BITBUCKET_USER_FUNCTION_REPO_BASE_URL =
  GIT_REPO_NAMESPACE_USER_FUNCTION &&
  `${BITBUCKET_BASE_URL ?? ''}/projects/${encodeURIComponent(GIT_REPO_NAMESPACE_USER_FUNCTION)}/repos`

/**
 * Build and return a Bitbucket repository HTTP(s) URL
 * for the specified resource.
 *
 * @param type One of `'rule'`, `'policy'` or `'function'`.
 * @param name The name of the rule, policy or user function.
 * @returns A full Bitbucket URL (or `undefined` if an unknown `type` is passed along).
 */
export function buildBitbucketRepoUrl(type: TableDataType, name: string) {
  switch (type) {
    case 'rule':
      return buildBitbucketRuleRepoUrl(name)
    case 'policy':
      return buildBitbucketPolicyRepoUrl(name)
    case 'function':
      return buildBitbucketUserFunctionRepoUrl(name)
    default:
      return undefined
  }
}

export function buildBitbucketRuleRepoUrl(path?: string) {
  const repoPath = path?.trim() ?? ''
  return BITBUCKET_RULE_REPO_BASE_URL ? urlJoin(BITBUCKET_RULE_REPO_BASE_URL, repoPath) : repoPath
}

export function buildBitbucketPolicyRepoUrl(path?: string) {
  const repoPath = path?.trim() ?? ''
  return BITBUCKET_POLICY_REPO_BASE_URL ? urlJoin(BITBUCKET_POLICY_REPO_BASE_URL, repoPath) : repoPath
}

export function buildBitbucketUserFunctionRepoUrl(path?: string) {
  const repoPath = path?.trim() ?? ''
  return BITBUCKET_USER_FUNCTION_REPO_BASE_URL ? urlJoin(BITBUCKET_USER_FUNCTION_REPO_BASE_URL, repoPath) : repoPath
}

/**
 * Build and return a Bitbucket Git-ref compare URL.
 *
 * @param type One of `'rule'`, `'policy'` or `'function'`.
 * @param name The name of the rule, policy or user function.
 * @param source The source branch or commit hash.
 * @param target The target branch or commit hash.
 * @returns A full GitHub compare URL.
 */
export function buildBitbucketCompareUrl(type: TableDataType, name: string, source: string, target: string): string {
  const repoUrl = buildBitbucketRepoUrl(type, name) ?? ''
  return `${repoUrl}/compare/diff?sourceBranch=${encodeURIComponent(source)}&targetBranch=${encodeURIComponent(target)}`
}
